/**
 * The notification type catalog (registry notifications.delivery-pipeline; slices.yaml notifications-core notes:
 * "every type names its emitting slice"). Isomorphic and pure: the server's notify rules take a type's category
 * and grouping rank from here, and the web renderers take its icon. A type family is emitted only once its owning
 * feature exists, so `EMITTED_FAMILIES` lists the subject families Quadratic writes at P0 — initiative*, document*,
 * teamUpdate*, pullRequest* and customer* never appear (delivery-pipeline AC6).
 *
 * The `detail` token: notification rows carry no rendered text (titles render per recipient at read time, AC4).
 * The few facts a renderer can't recover from the current subject are stored as a short machine token in the row's
 * `detail` column (`^[a-z0-9][a-z0-9_.:+-]{0,63}$`; ADR-0061, migration 0008):
 *
 *   issueStatusChanged   'completed' | 'canceled'           what the status change was, at the time it happened
 *   issueDue             'soon-<YYYY-MM-DD>' | 'overdue-<YYYY-MM-DD>'   which trigger fired for which due date
 *                        (also the idempotency key of issue.dueReminder: `notification_due_uq` makes one row per
 *                        recipient, issue and token, and app.notify skips a repeat)
 *
 * `reaction_emoji` keeps its one meaning: the emoji shortcode of reaction types (notifications-discussion, W4).
 */
import type { NOTIFICATION_CATEGORIES } from '../models';

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/** Slices that emit notification types (slices.yaml). */
export type EmittingSlice = 'notifications-core' | 'notifications-discussion' | 'notifications-projects';

/** `inbox.row.type-icon[data-value]` values; `generic` for a type this build doesn't know (AC16). */
export type NotificationIcon =
  | 'assigned'
  | 'unassigned'
  | 'comment'
  | 'mention'
  | 'reaction'
  | 'resolved'
  | 'completed'
  | 'canceled'
  | 'reopened'
  | 'urgent'
  | 'blocked'
  | 'unblocked'
  | 'due'
  | 'created'
  | 'added'
  | 'subscribed'
  | 'unsubscribed'
  | 'member'
  | 'lead'
  | 'description'
  | 'update'
  /** W4 step 0 (notifications-projects N2): a subscribed project's status / target date changed */
  | 'status'
  | 'date'
  | 'generic';

export interface NotificationTypeDef {
  readonly type: string;
  readonly category: NotificationCategory;
  readonly subject: 'issue' | 'project';
  readonly emitter: EmittingSlice;
  /** default icon; `iconFor` refines it from the row's detail token */
  readonly icon: NotificationIcon;
  /**
   * `grouping_priority`: how meaningful the type is as its group's preview line (higher wins; the inbox prefers a new
   * comment over reactions, inbox.notification-row AC3)
   */
  readonly rank: number;
  /** a mention outranks a comment for the same event and recipient (one notification per event, slices.yaml) */
  readonly precedence: number;
}

const def = (
  type: string,
  category: NotificationCategory,
  emitter: EmittingSlice,
  icon: NotificationIcon,
  rank: number,
  precedence = 0,
): NotificationTypeDef => ({ type, category, subject: type.startsWith('project') ? 'project' : 'issue', emitter, icon, rank, precedence });

/** Every type Quadratic emits at P0, with its preference category (delivery-pipeline AC6) and its emitting slice. */
export const NOTIFICATION_TYPES: readonly NotificationTypeDef[] = [
  // assignments: assignments, unassignments and membership changes
  def('issueAssignedToYou', 'assignments', 'notifications-core', 'assigned', 35),
  def('issueUnassignedFromYou', 'assignments', 'notifications-core', 'unassigned', 30),
  def('projectAddedAsMember', 'assignments', 'notifications-projects', 'member', 30),
  def('projectAddedAsLead', 'assignments', 'notifications-projects', 'lead', 30),
  // statusChanges: status, priority and blocking changes
  def('issueStatusChanged', 'statusChanges', 'notifications-core', 'completed', 28),
  def('issueReopened', 'statusChanges', 'notifications-core', 'reopened', 28),
  def('issuePriorityUrgent', 'statusChanges', 'notifications-core', 'urgent', 29),
  def('issueBlocking', 'statusChanges', 'notifications-core', 'blocked', 27),
  def('issueUnblocked', 'statusChanges', 'notifications-core', 'unblocked', 27),
  // reminders
  def('issueDue', 'reminders', 'notifications-core', 'due', 26),
  // subscriptions: entity subscriptions (team, project, cycle, label, view, user) and subscriber changes
  def('issueCreated', 'subscriptions', 'notifications-core', 'created', 20, 2),
  def('issueAddedToView', 'subscriptions', 'notifications-core', 'added', 19, 1),
  def('issueSubscribed', 'subscriptions', 'notifications-discussion', 'subscribed', 15),
  def('issueUnsubscribed', 'subscriptions', 'notifications-discussion', 'unsubscribed', 15),
  // commentsAndReplies: comments, replies and thread resolutions
  def('issueNewComment', 'commentsAndReplies', 'notifications-discussion', 'comment', 40, 1),
  def('issueThreadResolved', 'commentsAndReplies', 'notifications-discussion', 'resolved', 33),
  def('projectNewComment', 'commentsAndReplies', 'notifications-discussion', 'comment', 40, 1),
  def('projectUpdateNewComment', 'commentsAndReplies', 'notifications-discussion', 'comment', 40, 1),
  // mentions: in comments or content
  def('issueCommentMention', 'mentions', 'notifications-discussion', 'mention', 50, 2),
  def('issueMention', 'mentions', 'notifications-discussion', 'mention', 50, 2),
  def('projectMention', 'mentions', 'notifications-discussion', 'mention', 50, 2),
  def('projectUpdateMention', 'mentions', 'notifications-discussion', 'mention', 50, 2),
  // reactions
  def('issueCommentReaction', 'reactions', 'notifications-discussion', 'reaction', 10),
  def('issueEmojiReaction', 'reactions', 'notifications-discussion', 'reaction', 10),
  def('projectUpdateReaction', 'reactions', 'notifications-discussion', 'reaction', 10),
  // documentChanges
  def('projectDescriptionContentChange', 'documentChanges', 'notifications-projects', 'description', 18),
  def('projectMilestoneDescriptionContentChange', 'documentChanges', 'notifications-projects', 'description', 18),
  // postsAndUpdates
  def('projectUpdateCreated', 'postsAndUpdates', 'notifications-projects', 'update', 40),
  // subscriptions on a project (W4 step 0, notifications-projects N2; registry projects.subscribe AC2)
  def('projectStatusChanged', 'subscriptions', 'notifications-projects', 'status', 20),
  def('projectTargetDateChanged', 'subscriptions', 'notifications-projects', 'date', 20),
];

const BY_TYPE: ReadonlyMap<string, NotificationTypeDef> = new Map(NOTIFICATION_TYPES.map((t) => [t.type, t]));

/** The catalog entry of a type, or undefined for a type this build doesn't know (a newer server, AC16). */
export function notificationType(type: string): NotificationTypeDef | undefined {
  return BY_TYPE.get(type);
}

/** Subject families Quadratic emits at P0 (delivery-pipeline AC6: no unbuilt family is ever emitted). */
export const EMITTED_FAMILIES = ['issue', 'project'] as const;
/** Families whose owning features are unbuilt at P0. */
export const UNBUILT_FAMILIES = ['initiative', 'document', 'teamUpdate', 'pullRequest', 'customer'] as const;

/** The types notifications-core itself emits (its after-write rules and issue.dueReminder). */
export const CORE_TYPES: readonly string[] = NOTIFICATION_TYPES.filter((t) => t.emitter === 'notifications-core').map((t) => t.type);

/** `inbox.row.type-icon[data-value]` for a row (`generic` for unknown types). */
export function iconFor(type: string, detail: string | null | undefined): NotificationIcon {
  const t = BY_TYPE.get(type);
  if (!t) return 'generic';
  if (type === 'issueStatusChanged' && detail === 'canceled') return 'canceled';
  return t.icon;
}

// ---------------------------------------------------------------------------------------------------------------
// detail tokens

export type StatusDetail = 'completed' | 'canceled';
export type DueTrigger = 'soon' | 'overdue';

const DUE_TOKEN = /^(soon|overdue)-(\d{4}-\d{2}-\d{2})$/;

/** The issueDue detail token for a trigger and due date (`soon-2026-04-02`). */
export function dueToken(trigger: DueTrigger, dueDate: string): string {
  return `${trigger}-${dueDate}`;
}

/** Parse an issueDue detail token; null when it isn't one. */
export function parseDueToken(token: string | null | undefined): { trigger: DueTrigger; dueDate: string } | null {
  const m = typeof token === 'string' ? DUE_TOKEN.exec(token) : null;
  return m ? { trigger: m[1] as DueTrigger, dueDate: m[2]! } : null;
}

/** The grouping key of a notification about an issue or a project (inbox.entity-grouping: one row per entity). */
export function groupingKey(subject: { issueId?: string | null; projectId?: string | null }): string {
  if (subject.issueId) return `issue:${subject.issueId}`;
  if (subject.projectId) return `project:${subject.projectId}`;
  throw new Error('groupingKey: no subject');
}
